<template>
	<AppDialog :disableClose="localDisableClose" width="620" :closeTop="'-30'" :closeRight="'-10'" dialogName="PublishWorkflowDialog">
		<Col class="dialog-content rounded">
			<ShareFeatureDialogHeader :service="service" :workflow="workflow" />
			<PublishingAnimation class="mt-n12" :isPublishCompleted="isPublishCompleted" @animation-completed="onAnimationCompleted" />
		</Col>
	</AppDialog>
</template>
<script>
import { ProjectPermissions } from '@ply-io/ply-front-shared-utilities';

import { BillingErrorCodes } from '@/components/Billing/billing-types';
import PublishingAnimation from '@/components/Project/PublishingAnimation.vue';
import PublishButtonService from '@/components/Project/publish-button-service.js';
import AppDialog from '@/components/dialogs/AppDialog/AppDialog.vue';
import AppDialogManagerService from '@/components/dialogs/AppDialogManager/app-dialog-manager-service';
import ShareFeatureDialogHeader from '@/components/dialogs/ShareFeatureDialog/ShareFeatureDialogHeader.vue';
import { getServiceBySlug } from '@/store/modules/apps/getters';
import { openSuggestionsToMoveCredentialsToUserConfiguration } from '@/store/modules/credentials/actions';
import { isConfigurationsOn, isInstallablesOn } from '@/store/modules/featureFlags/getters';
import { projectHasPermission } from '@/store/modules/projects/getters';
import { selectWorkflowId, publishWorkflow, setPublishLoading, fetchSharedData } from '@/store/modules/selectedWorkflow/actions';
import { editWorkflowHasInstallable, isSharedWithOthers, isPublishLoading } from '@/store/modules/selectedWorkflow/getters';
import {
	generalConfigurationId,
	userSpecificConfigurationId,
	publishedGeneralConfigurationId,
	publishedUserSpecificConfigurationId,
} from '@/store/modules/selectedWorkflow/modules/configurations/getters';
import { canEditWorkflow, getWorkflow } from '@/store/modules/workflows/getters';
import { mapActionsShorthand, mapGettersShorthand } from '@/store/utils/mappers';
import WorkflowService from '@/utils/WorkflowService';
import ErrorHandlingService, { TaskProcessError } from '@/utils/error-handling-service';
import { WORKFLOW_ERROR_CODES } from '@/utils/workflow-service.const';

export default {
	name: 'PublishWorkflowDialog',
	components: {
		AppDialog,
		PublishingAnimation,
		ShareFeatureDialogHeader,
	},
	props: {
		workflowId: { type: Number },
		disableClose: { type: Boolean },
		isLoadedAsIframe: { type: Boolean },
		dialogPromiseHandlers: { type: Object },
		showCloseBuilderButton: { type: Boolean },
		closeBuilder: { type: Function },
	},
	computed: {
		...mapGettersShorthand({
			isPublishLoading,
			getServiceBySlug,
			isInstallablesOn,
			isConfigurationsOn,
			isSharedWithOthers,
			projectHasPermission,
			canEditWorkflow,
			generalConfigurationId,
			useWorkflow: getWorkflow,
			editWorkflowHasInstallable,
			userSpecificConfigurationId,
			publishedGeneralConfigurationId,
			publishedUserSpecificConfigurationId,
		}),
		workflow() {
			return this.useWorkflow(this.workflowId) || {};
		},
		service() {
			return this.contextServiceSlug ? this.getServiceBySlug(this.contextServiceSlug) : null;
		},
		contextServiceSlug() {
			return this.workflow.context_service_slug;
		},
		canShare() {
			return !!this.workflow?.current_version && this.workflow?.is_published && this.hasSharePermissions;
		},
		hasSharePermissions() {
			return (
				this.canEditWorkflow(this.workflow.id) || this.projectHasPermission(this.workflow.project, ProjectPermissions.ChangeWorkflowUsers)
			);
		},
		localDisableClose() {
			return this.disableClose || this.isPublishLoading;
		},
		isWorkflowShareable() {
			return WorkflowService.isWorkflowShareable(this.workflow);
		},
	},
	methods: {
		...mapActionsShorthand({
			publishWorkflow,
			fetchSharedData,
			selectWorkflowId,
			setPublishLoading,
			openSuggestionsToMoveCredentialsToUserConfiguration,
		}),
		close() {
			this.dialogPromiseHandlers.resolve();
			AppDialogManagerService.closeCurrentDialog();
		},
		onAnimationCompleted() {
			this.close();
			if (this.isWorkflowShareable) {
				AppDialogManagerService.openShareDialog({
					isPostPublishMode: true,
					canShare: this.canShare,
					workflowId: this.workflowId,
					disableClose: this.disableClose,
					showCloseBuilderButton: this.showCloseBuilderButton,
					closeBuilder: this.closeBuilder,
				});
			}
		},
		async getPublishFlags() {
			const publishFlags = {};
			try {
				this.setPublishLoading(true);
				Object.assign(
					publishFlags,
					await PublishButtonService.getWorkflowPublishFlags({
						workflow: this.workflow,
						fetchSharedData: this.fetchSharedData,
						isInstallablesOn: this.isInstallablesOn,
						isConfigurationsOn: this.isConfigurationsOn,
						workflowIsPublished: this.workflow.is_published,
						getWorkflowSharedData: () => this.isSharedWithOthers,
						workflowHasInstallable: this.editWorkflowHasInstallable,
						workflowHasGeneralConfigurations: !!this.generalConfigurationId,
						workflowHasPersonalConfigurations: !!this.userSpecificConfigurationId,
						publishedWorkflowVersionHasGeneralConfigurations: !!this.publishedGeneralConfigurationId,
						publishedWorkflowVersionHasPersonalConfigurations: !!this.publishedUserSpecificConfigurationId,
					})
				);
			} catch (err) {
				if (!err) {
					this.$toast.error('Error has occurred while trying to publish the workflow');
				}
				throw new Error(err);
			} finally {
				this.setPublishLoading(false);
			}
			return publishFlags;
		},
		async publishFlow() {
			try {
				const publishFlags = {};
				try {
					this.setPublishLoading(true);
					Object.assign(publishFlags, await this.getPublishFlags());
				} catch (err) {
					return;
				} finally {
					this.setPublishLoading(false);
				}
				this.setPublishLoading(true);
				await this.publishWorkflow(publishFlags);
				this.isPublishCompleted = true;
				if (!this.disableClose) {
					this.$toast.success('New workflow version published successfully');
				}
			} catch (err) {
				if (
					ErrorHandlingService.extractErrorMetadata(err) &&
					ErrorHandlingService.extractErrorCode(err) === WORKFLOW_ERROR_CODES.PUBLISH_MISSING_DEPENDENCY_NODES
				) {
					await AppDialogManagerService.openPublishFlowErrorsDialog({
						erroredNodes: ErrorHandlingService.extractErrorMetadata(err),
					});
				} else {
					const didPublishFailBecauseBillingPlanUpgradeRequired =
						err instanceof TaskProcessError && err.code === BillingErrorCodes.UpgradeSubscription;
					const msg =
						(ErrorHandlingService.extractStatusCode(err) === 400 && ErrorHandlingService.extractErrorMessage(err)) ||
						(err instanceof TaskProcessError && ErrorHandlingService.extractErrorMessage(err)) ||
						'Error has occurred while trying to publish the workflow';
					if (didPublishFailBecauseBillingPlanUpgradeRequired) {
						this.close();
						AppDialogManagerService.openPublishFailedBecauseBillingPlanDialog({
							detail: err.metadata.detail,
							reasons: err.metadata.reasons,
							planRequired: err.metadata.plan_required,
						});
					} else {
						this.$toast.error(msg);
						this.close();
					}
				}
			} finally {
				this.setPublishLoading(false);
			}
		},
	},
	async created() {
		this.setPublishLoading(true);
		await this.selectWorkflowId({ id: this.workflowId });
		await this.openSuggestionsToMoveCredentialsToUserConfiguration({
			isFromPublish: true,
			workflow: this.workflow,
			disableClose: this.disableClose,
			next: () => (this.didAnswerSuggestions = true),
		});
		if (!this.didAnswerSuggestions) {
			return this.close();
		}
		await this.publishFlow();
	},
	data() {
		return {
			isPublishCompleted: false,
			didAnswerSuggestions: false,
		};
	},
	beforeDestroy() {
		this.setPublishLoading(false);
	},
};
</script>
<style scoped lang="scss">
.dialog-content {
	height: 690px;
	background: var(--white);
}
.rounded {
	border-radius: 15px !important;
}
</style>
